import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { useCreateProperty, usePropertyOptions } from '@/admin/hooks/use-properties'
import { toContactInput, toGroupAddress } from '@/admin/lib/property-group-forms'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm, type FormContext, type FormField } from '@/shared/lib/forms'

const FORM_ID = 'new-property-form'

/**
 * Create a property outside a registration or claim: where it is, what type
 * it is, which HOA it belongs to (optional), who owns it and where their mail
 * goes, plus an optional second owner.
 */
export function NewPropertyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: options, isLoading } = usePropertyOptions()
  const createProperty = useCreateProperty()

  const fields = useMemo<FormField[]>(() => {
    const groupOptions = options?.groupOptions ?? []
    const mailingDiffers = (ctx: FormContext) => ctx.values.mailing_same === 'no'
    const groupParam = searchParams.get('group') ?? ''
    return [
      {
        type: 'address',
        name: 'address',
        label: t('property.new.address', 'Property Address'),
        value: null,
        required: t('property.new.addressRequired', 'Property address is required.'),
        configurationData: { lookup_provider: 'address' },
      },
      {
        type: 'select',
        name: 'group_uuid',
        label: t('property.new.group', '{{groupNoun}}'),
        placeholder: t('property.new.groupPlaceholder', 'Not part of a {{groupNoun}}'),
        value: groupParam,
        options: groupOptions.map((h) => ({ value: h.uuid, label: h.name })),
        visible: groupOptions.length > 0,
      },
      {
        type: 'select',
        name: 'property_type',
        label: t('property.new.type', 'Property Type'),
        // A property added from an HOA starts on the tenant's HOA type.
        value: groupParam ? (options?.default_property_type ?? '') : '',
        required: t('property.new.typeRequired', 'Property type is required.'),
        options: options?.property_type_options ?? [],
      },
      {
        type: 'text',
        name: 'owner_first_name',
        label: t('property.new.ownerFirst', 'Owner First Name'),
        value: '',
        required: t('property.new.ownerFirstRequired', 'First name is required.'),
      },
      {
        type: 'text',
        name: 'owner_last_name',
        label: t('property.new.ownerLast', 'Owner Last Name'),
        value: '',
        required: t('property.new.ownerLastRequired', 'Last name is required.'),
      },
      {
        type: 'email',
        name: 'owner_email',
        label: t('property.new.ownerEmail', 'Owner Email'),
        value: '',
        hint: t('property.new.emailOrPhone', 'Email or phone is required.'),
      },
      {
        type: 'phone',
        name: 'owner_phone',
        label: t('property.new.ownerPhone', 'Owner Phone'),
        value: '',
      },
      {
        type: 'select',
        name: 'mailing_same',
        label: t(
          'property.new.mailingSameQuestion',
          "Is the owner's mailing address the same as the property address?",
        ),
        value: 'yes',
        required: t('property.new.mailingSameRequired', 'Choose Yes or No.'),
        options: [
          { value: 'yes', label: t('common.yes', 'Yes') },
          { value: 'no', label: t('common.no', 'No') },
        ],
      },
      {
        type: 'address',
        name: 'mailing_address',
        label: t('property.new.mailingAddress', 'Owner Mailing Address'),
        value: null,
        required: t('property.new.mailingAddressRequired', 'Mailing address is required.'),
        configurationData: { lookup_provider: 'address' },
        visible: mailingDiffers,
      },
      {
        type: 'text',
        name: 'co_first_name',
        label: t('property.new.coFirst', 'Second Owner First Name (optional)'),
        value: '',
      },
      {
        type: 'text',
        name: 'co_last_name',
        label: t('property.new.coLast', 'Second Owner Last Name'),
        value: '',
      },
      {
        type: 'email',
        name: 'co_email',
        label: t('property.new.coEmail', 'Second Owner Email'),
        value: '',
      },
      {
        type: 'phone',
        name: 'co_phone',
        label: t('property.new.coPhone', 'Second Owner Phone'),
        value: '',
      },
    ]
  }, [t, options, searchParams])

  const { Form } = useForm({ name: FORM_ID, submit: 'footer', fields }, async (values) => {
    const v = values as Record<string, unknown>
    const pick = (prefix: string) =>
      toContactInput({
        first_name: v[`${prefix}_first_name`],
        last_name: v[`${prefix}_last_name`],
        email: v[`${prefix}_email`],
        phone: v[`${prefix}_phone`],
      })
    const coOwner = pick('co')
    try {
      const created = await createProperty.mutateAsync({
        address: toGroupAddress(v.address),
        property_type: String(v.property_type ?? ''),
        owner: pick('owner'),
        mailing_address:
          v.mailing_same === 'no' ? toGroupAddress(v.mailing_address) : null,
        co_owner: Object.values(coOwner).some(Boolean) ? coOwner : null,
        group_uuid: v.group_uuid ? String(v.group_uuid) : null,
      })
      toast.success(t('property.new.created', 'Property created'))
      navigate(`/properties/${created.uuid}`)
    } catch (err) {
      toastApiError(err, t('property.new.failed', 'Could not create the property'))
    }
  })

  return (
    <AppShell>
      <main className="mx-auto max-w-2xl px-6 py-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 cursor-pointer gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('common.back', 'Back')}
        </Button>
        <h1 className="clara-title mb-1">{t('property.new.title', 'New Property')}</h1>
        <p className="mb-8 text-[12px] text-[var(--ink-3)]">
          {t(
            'property.new.description',
            'Add a property before anything is registered or claimed on it. Registrations and claims you start from the property page come pre-filled.',
          )}
        </p>
        {isLoading ? (
          <p className="text-[12px] text-[var(--ink-3)]">{t('common.loading', 'Loading...')}</p>
        ) : (
          <Form className="flex flex-col gap-4" />
        )}
        <Separator className="my-8" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)} className="cursor-pointer">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={createProperty.isPending || isLoading}
            className="cursor-pointer gap-1.5"
          >
            {createProperty.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('property.new.submit', 'Create Property')}
          </Button>
        </div>
      </main>
    </AppShell>
  )
}
